import type { HTMLAttributes, KeyboardEvent, ReactNode } from 'react';
import { cn } from '../cn.js';

export type CardTone = 'default' | 'success' | 'warning' | 'danger' | 'info';

const TONES: Record<CardTone, string> = {
  default: 'bg-surface border-line',
  success: 'bg-success-bg border-success-border',
  warning: 'bg-warning-bg border-warning-border',
  danger: 'bg-danger-bg border-danger-border',
  info: 'bg-info-bg border-info-border',
};

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  tone?: CardTone;
  /** Adds hover affordance. Also makes the card keyboard-operable. */
  onActivate?: () => void;
}

export function Card({ tone = 'default', onActivate, className, children, ...rest }: CardProps) {
  const interactive = Boolean(onActivate);

  return (
    <div
      className={cn(
        'rounded-xl border p-5 shadow-card',
        TONES[tone],
        interactive && 'cursor-pointer hover:shadow-md transition-shadow duration-150',
        className,
      )}
      // A clickable div needs an explicit role, a tab stop and keyboard
      // activation, or it is invisible to keyboard and screen-reader users.
      {...(interactive
        ? {
            role: 'button',
            tabIndex: 0,
            onClick: onActivate,
            onKeyDown: (e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onActivate?.();
              }
            },
          }
        : {})}
      {...rest}
    >
      {children}
    </div>
  );
}

export interface StatCardProps {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: CardTone;
  onActivate?: () => void;
}

/** The dashboard metric tile from the reference application. */
export function StatCard({ label, value, hint, tone = 'default', onActivate }: StatCardProps) {
  const valueTone =
    tone === 'danger'
      ? 'text-danger-fg'
      : tone === 'warning'
        ? 'text-warning-fg'
        : tone === 'success'
          ? 'text-success-fg'
          : 'text-body';

  return (
    <Card tone={tone} onActivate={onActivate} className="flex flex-col gap-2">
      <div className="flex items-start justify-between">
        <span className="text-sm font-medium text-muted">{label}</span>
      </div>
      <div className={cn('text-3xl font-bold tabular-nums', valueTone)}>{value}</div>
      {hint ? <div className="text-xs text-subtle">{hint}</div> : null}
    </Card>
  );
}

// `title` is omitted from the DOM attributes because the panel heading is
// rich content, not the string that HTMLElement.title (the tooltip) accepts.
export interface PanelProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** Removes body padding, for panels containing a grid or table. */
  flush?: boolean;
}

/** A titled content panel: the two-column dashboard sections in the reference. */
export function Panel({
  title,
  description,
  actions,
  flush = false,
  className,
  children,
  ...rest
}: PanelProps) {
  return (
    <section
      className={cn(
        'bg-surface border border-line rounded-xl shadow-card',
        flush ? 'overflow-hidden' : 'p-5',
        className,
      )}
      {...rest}
    >
      {(title || actions) && (
        <div
          className={cn(
            'flex items-start justify-between gap-3 flex-wrap',
            flush ? 'px-5 pt-5 pb-3' : 'mb-3',
          )}
        >
          <div className="min-w-0">
            {title ? <h2 className="text-base font-semibold text-body">{title}</h2> : null}
            {description ? <p className="text-sm text-muted mt-1">{description}</p> : null}
          </div>
          {actions ? <div className="flex items-center gap-2 shrink-0">{actions}</div> : null}
        </div>
      )}
      {children}
    </section>
  );
}
